import { useState, useEffect, useCallback } from 'react';
import '../styles/v2/pages/entry-fleet.css';
import { useNavigate } from 'react-router-dom';
import fetchWithTenant from '../utils/fetchWithTenant';
import {
  Tablet, Plus, RefreshCw, Search, Signal, SignalZero, AlertTriangle,
  CheckCircle, XCircle, Clock, Cpu, HardDrive, Wifi, WifiOff,
  ChevronRight, Settings, Send, Trash2, Eye, MoreVertical, HelpCircle
} from 'lucide-react';
import useTutorialStore from '../stores/tutorialStore';
import useTutorialPrompt from '../hooks/useTutorialPrompt';
import TutorialPrompt from '../components/tutorials/TutorialPrompt';
import entryFleetTutorial from '../components/tutorials/configs/entryFleetTutorial';
import { useBranding } from '../contexts/BrandingContext';

const STATUS_CONFIG = {
  ONLINE:         { color: '#22c55e', bg: 'rgba(34, 197, 94, 0.15)',  icon: Signal,       label: 'Online' },
  OFFLINE:        { color: '#ef4444', bg: 'rgba(239, 68, 68, 0.15)',  icon: SignalZero,    label: 'Offline' },
  UPDATING:       { color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.15)', icon: RefreshCw,     label: 'Updating' },
  ERROR:          { color: '#ef4444', bg: 'rgba(239, 68, 68, 0.15)',  icon: AlertTriangle, label: 'Error' },
  DECOMMISSIONED: { color: '#6b7280', bg: 'rgba(107, 114, 128, 0.15)', icon: XCircle,      label: 'Decommissioned' },
};

const CAMERA_HEALTH = {
  healthy:  { color: '#22c55e', label: 'Healthy' },
  degraded: { color: '#f59e0b', label: 'Degraded' },
  down:     { color: '#ef4444', label: 'Down' },
};

function timeAgo(dateStr) {
  if (!dateStr) return 'Never';
  const diff = Date.now() - new Date(dateStr).getTime();
  if (diff < 60000) return 'Just now';
  if (diff < 3600000) return Math.floor(diff / 60000) + 'm ago';
  if (diff < 86400000) return Math.floor(diff / 3600000) + 'h ago';
  return Math.floor(diff / 86400000) + 'd ago';
}

const EntryFleet = () => {
  const startTutorial = useTutorialStore((s) => s.startTutorial);
  const { showPrompt, dismissTutorial } = useTutorialPrompt(entryFleetTutorial);
  const { branding } = useBranding();
  const entryProductName = branding.isWhiteLabeled ? 'Entry' : 'Serverli Entry';

  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [license, setLicense] = useState(null);
  const [devices, setDevices] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [refreshing, setRefreshing] = useState(false);
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [enrollToken, setEnrollToken] = useState(null);
  const [enrollForm, setEnrollForm] = useState({ siteLabel: '', ttlHours: 24 });
  const [generatingToken, setGeneratingToken] = useState(false);
  const [enrollMode, setEnrollMode] = useState('qr'); // 'qr' | 'token'
  const [qrData, setQrData] = useState(null);
  const [loadingQr, setLoadingQr] = useState(false);
  const [qrPairing, setQrPairing] = useState(null); // pending pairing from QR scan
  const [acceptingQr, setAcceptingQr] = useState(false);
  const [pendingPairings, setPendingPairings] = useState([]);
  const [acceptingPairing, setAcceptingPairing] = useState(null);
  const [showPairings, setShowPairings] = useState(false);
  const [showDecommissioned, setShowDecommissioned] = useState(false);

  const fetchDashboard = useCallback(async () => {
    try {
      const [dashRes, devicesRes] = await Promise.all([
        fetchWithTenant('/api/entry/dashboard'),
        fetchWithTenant('/api/entry/devices'),
      ]);

      if (dashRes.ok) {
        const data = await dashRes.json();
        setStats(data.stats);
        setLicense(data.license);
      }
      if (devicesRes.ok) {
        const data = await devicesRes.json();
        setDevices(data.devices || []);
      }
    } catch (e) {
      console.error('Failed to fetch Entry dashboard:', e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  // Auto-refresh every 30s
  useEffect(() => {
    const interval = setInterval(() => fetchDashboard(), 30000);
    return () => clearInterval(interval);
  }, [fetchDashboard]);

  const fetchPendingPairings = useCallback(async () => {
    try {
      const res = await fetchWithTenant('/api/entry/pair/pending');
      if (res.ok) {
        const data = await res.json();
        setPendingPairings(data);
      }
    } catch (e) {
      console.error('Failed to fetch pending pairings', e);
    }
  }, []);

  useEffect(() => {
    fetchPendingPairings();
    const interval = setInterval(fetchPendingPairings, 15000);
    return () => clearInterval(interval);
  }, [fetchPendingPairings]);

  const handleAcceptPairing = async (pairingId) => {
    setAcceptingPairing(pairingId);
    try {
      const res = await fetchWithTenant(`/api/entry/pair/${pairingId}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (res.ok) {
        setPendingPairings(p => p.filter(r => r.id !== pairingId));
        fetchDashboard();
      } else {
        const err = await res.json();
        alert(err.error || 'Failed to accept pairing');
      }
    } catch (e) {
      alert('Network error');
    } finally {
      setAcceptingPairing(null);
    }
  };

  const handleRejectPairing = async (pairingId) => {
    try {
      await fetchWithTenant(`/api/entry/pair/${pairingId}`, { method: 'DELETE' });
      setPendingPairings(p => p.filter(r => r.id !== pairingId));
    } catch (e) {
      alert('Network error');
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchDashboard();
  };

  const handleFetchQr = async () => {
    setLoadingQr(true);
    setQrData(null);
    try {
      const res = await fetchWithTenant('/api/entry/pair/qr?ccmUrl=' + encodeURIComponent(window.location.origin));
      if (res.ok) {
        const data = await res.json();
        setQrData(data);
        setQrPairing(null);
        // Poll for tablet scan every 5s
        const pollId = setInterval(async () => {
          try {
            const pr = await fetchWithTenant('/api/entry/pair/pending');
            if (pr.ok) {
              const pairings = await pr.json();
              // Find the request matching our nonce that now has deviceInfo (tablet scanned)
              const match = pairings.find(p => p.pairingCode === data.nonce && p.deviceInfo && Object.keys(p.deviceInfo).length > 0);
              if (match) { setQrPairing(match); clearInterval(pollId); }
            }
          } catch(e) {}
        }, 5000);
        // Clear after 10 min
        setTimeout(() => clearInterval(pollId), 600000);
      } else {
        const err = await res.json();
        alert(err.error || 'Failed to generate QR code');
      }
    } catch (e) {
      alert('Network error generating QR code');
    } finally {
      setLoadingQr(false);
    }
  };

  const handleGenerateToken = async () => {
    setGeneratingToken(true);
    try {
      const res = await fetchWithTenant('/api/entry/tokens', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(enrollForm),
      });
      if (res.ok) {
        const data = await res.json();
        setEnrollToken(data.token);
      } else {
        const err = await res.json();
        alert(err.error || 'Failed to generate token');
      }
    } catch (e) {
      console.error(e);
    } finally {
      setGeneratingToken(false);
    }
  };

  const copyToken = () => {
    if (enrollToken?.token) {
      navigator.clipboard.writeText(enrollToken.token);
    }
  };

  const decommissionedCount = devices.filter(d => d.status === 'DECOMMISSIONED').length;

  // Filter devices
  const filtered = devices.filter(d => {
    if (d.status === 'DECOMMISSIONED' && !showDecommissioned && statusFilter !== 'DECOMMISSIONED') return false;
    if (statusFilter !== 'ALL' && d.status !== statusFilter) return false;
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      return d.name?.toLowerCase().includes(s) ||
        d.siteLabel?.toLowerCase().includes(s) ||
        d.deviceId?.toLowerCase().includes(s) ||
        d.ipAddress?.toLowerCase().includes(s);
    }
    return true;
  });

  if (loading) {
    return (
      <div className="entry-fleet">
        <div className="entry-loading">
          <RefreshCw size={24} className="spin" />
          <span>Loading fleet...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="entry-fleet">
      {/* Header */}
      <div className="entry-header">
        <div className="entry-header-left">
          <h1><Tablet size={24} /> {entryProductName}</h1>
          <span className="entry-header-sub">Fleet Management</span>
        </div>
        <div className="entry-header-actions">
          <button className="btn btn-secondary" onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw size={16} className={refreshing ? 'spin' : ''} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
          <button className="btn btn-primary" onClick={() => { setEnrollToken(null); setShowEnrollModal(true); }}>
            <Plus size={16} /> Enroll Tablet
          </button>
          <button className="tutorial-help-btn" onClick={() => startTutorial(entryFleetTutorial.module, entryFleetTutorial.steps)} title="Take a tour" aria-label="Take a tour">
            <HelpCircle size={16} />
          </button>
        </div>
      </div>

      {/* Grace Period Warning */}
      {license?.inGracePeriod && (
        <div className="entry-grace-banner">
          <span style={{ fontWeight: 600 }}>License Expired</span>
          <span>Your Entry license has expired. Tablets will be locked in {license.graceDaysRemaining} day{license.graceDaysRemaining !== 1 ? 's' : ''}. Please renew to avoid service interruption.</span>
        </div>
      )}

      {/* License Banner */}
      {license && (
        <div className="entry-license-banner">
          <div className="entry-license-info">
            <span className="entry-license-sku">{license.sku}</span>
            <span className="entry-license-status" style={{
              color: license.status === 'ACTIVE' ? '#22c55e' : license.status === 'TRIAL' ? '#a855f7' : '#ef4444'
            }}>{license.status}</span>
          </div>
          <div className="entry-license-usage">
            <span>Devices: {license.usage?.devices || 0} / {license.maxDevices >= 9999 ? '\u221E' : license.maxDevices}</span>
            <span>Profiles: {license.usage?.profiles || 0} / {license.maxProfiles >= 999 ? '\u221E' : license.maxProfiles}</span>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      {stats && (
        <div className="entry-stats">
          <div className={"entry-stat-card" + (statusFilter === 'ALL' ? ' active' : '')}
               onClick={() => setStatusFilter('ALL')}>
            <div className="entry-stat-value">{stats.devices?.total || 0}</div>
            <div className="entry-stat-label">Total Devices</div>
          </div>
          <div className={"entry-stat-card" + (statusFilter === 'ONLINE' ? ' active' : '')}
               onClick={() => setStatusFilter(statusFilter === 'ONLINE' ? 'ALL' : 'ONLINE')}>
            <div className="entry-stat-value" style={{ color: '#22c55e' }}>{stats.devices?.online || 0}</div>
            <div className="entry-stat-label">Online</div>
          </div>
          <div className={"entry-stat-card" + (statusFilter === 'OFFLINE' ? ' active' : '')}
               onClick={() => setStatusFilter(statusFilter === 'OFFLINE' ? 'ALL' : 'OFFLINE')}>
            <div className="entry-stat-value" style={{ color: '#ef4444' }}>{stats.devices?.offline || 0}</div>
            <div className="entry-stat-label">Offline</div>
          </div>
          <div className="entry-stat-card">
            <div className="entry-stat-value" style={{ color: '#3b82f6' }}>{stats.events24h?.total || 0}</div>
            <div className="entry-stat-label">Events (24h)</div>
          </div>
          <div className="entry-stat-card">
            <div className="entry-stat-value" style={{ color: '#22c55e' }}>{stats.events24h?.granted || 0}</div>
            <div className="entry-stat-label">Granted</div>
          </div>
          <div className="entry-stat-card">
            <div className="entry-stat-value" style={{ color: '#ef4444' }}>{stats.events24h?.denied || 0}</div>
            <div className="entry-stat-label">Denied</div>
          </div>
        </div>
      )}

      {/* Pending Pairings */}
      {pendingPairings.length > 0 && (
        <div className="entry-pending-pairings">
          <div className="entry-pending-header" onClick={() => setShowPairings(s => !s)}>
            <span className="entry-pending-badge">{pendingPairings.length}</span>
            <span>Tablet Pairing Request{pendingPairings.length !== 1 ? 's' : ''} Pending</span>
            <span style={{ marginLeft: 'auto', fontSize: '12px', color: '#888' }}>{showPairings ? '▲ Hide' : '▼ Show'}</span>
          </div>
          {showPairings && (
            <div className="entry-pending-list">
              {pendingPairings.map(req => {
                const info = req.deviceInfo || {};
                const expiresIn = Math.max(0, Math.round((new Date(req.expiresAt) - Date.now()) / 1000 / 60));
                return (
                  <div key={req.id} className="entry-pending-item">
                    <div className="entry-pending-code">{req.pairingCode}</div>
                    <div className="entry-pending-info">
                      <div className="entry-pending-device">{info.manufacturer} {info.model}</div>
                      <div className="entry-pending-meta">
                        {info.ipAddress && <span>IP: {info.ipAddress}</span>}
                        {info.appVersion && <span>v{info.appVersion}</span>}
                        <span style={{ color: expiresIn < 3 ? '#ef4444' : '#888' }}>Expires in {expiresIn}m</span>
                      </div>
                    </div>
                    <div className="entry-pending-actions">
                      <button
                        className="btn btn-success btn-sm"
                        onClick={() => handleAcceptPairing(req.id)}
                        disabled={acceptingPairing === req.id}
                      >
                        {acceptingPairing === req.id ? 'Accepting...' : '✓ Accept'}
                      </button>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleRejectPairing(req.id)}
                      >
                        ✕ Reject
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      {/* Search + Filter */}
      <div className="entry-toolbar">
        <div className="entry-search">
          <Search size={16} />
          <input
            type="text"
            placeholder="Search devices..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
        {decommissionedCount > 0 && (
          <button
            className={'btn btn-sm entry-decom-toggle' + (showDecommissioned ? ' active' : '')}
            onClick={() => setShowDecommissioned(v => !v)}
            title={showDecommissioned ? 'Hide decommissioned devices' : 'Show decommissioned devices'}
          >
            {showDecommissioned ? 'Hide decommissioned' : 'Show decommissioned'}
            <span className="entry-decom-toggle-count">{decommissionedCount}</span>
          </button>
        )}
      </div>

      {/* Device List */}
      {filtered.length === 0 ? (
        <div className="entry-empty">
          <Tablet size={48} style={{ opacity: 0.3 }} />
          <h3>{devices.length === 0 ? 'No Tablets Enrolled' : 'No Matching Devices'}</h3>
          <p>{devices.length === 0
            ? 'Generate an enrollment token and add your first Entry tablet.'
            : 'Try adjusting your search or filter.'}</p>
          {devices.length === 0 && (
            <button className="btn btn-primary" onClick={() => { setEnrollToken(null); setShowEnrollModal(true); }}>
              <Plus size={16} /> Enroll First Tablet
            </button>
          )}
        </div>
      ) : (
        <div className="entry-device-grid">
          {filtered.map(device => {
            const isDecom = device.status === 'DECOMMISSIONED';
            const sc = STATUS_CONFIG[device.status] || STATUS_CONFIG.OFFLINE;
            const StatusIcon = sc.icon;
            const ch = device.cameraHealth ? CAMERA_HEALTH[device.cameraHealth] : null;

            return (
              <div
                key={device.id}
                className={'entry-device-card' + (isDecom ? ' entry-device-card-decommissioned' : '')}
                onClick={() => navigate('/entry/device/' + device.id)}
              >
                <div className="entry-device-header">
                  <div className="entry-device-name-row">
                    <Tablet size={18} style={{ opacity: 0.5 }} />
                    <span className="entry-device-name">{device.name}</span>
                  </div>
                  {isDecom ? (
                    <span className="entry-device-status entry-device-status-decommissioned">
                      <XCircle size={12} />
                      Decommissioned
                    </span>
                  ) : (
                    <span className="entry-device-status" style={{ background: sc.bg, color: sc.color }}>
                      <StatusIcon size={12} />
                      {sc.label}
                    </span>
                  )}
                </div>

                <div className="entry-device-meta">
                  {device.siteLabel && (
                    <div className="entry-device-meta-item">
                      <span className="meta-label">Site</span>
                      <span className="meta-value">{device.siteLabel}</span>
                    </div>
                  )}
                  {device.ipAddress && (
                    <div className="entry-device-meta-item">
                      <span className="meta-label">IP</span>
                      <span className="meta-value">{device.ipAddress}</span>
                    </div>
                  )}
                  {device.manufacturer && (
                    <div className="entry-device-meta-item">
                      <span className="meta-label">Hardware</span>
                      <span className="meta-value">{device.manufacturer} {device.model || ''}</span>
                    </div>
                  )}
                  {device.appVersion && (
                    <div className="entry-device-meta-item">
                      <span className="meta-label">Version</span>
                      <span className="meta-value">{device.appVersion}</span>
                    </div>
                  )}
                  {device.profile && (
                    <div className="entry-device-meta-item">
                      <span className="meta-label">Profile</span>
                      <span className="meta-value">{device.profile.name}</span>
                    </div>
                  )}
                </div>

                {/* Health Indicators */}
                {!isDecom && (
                  <div className="entry-device-health">
                    {ch && (
                      <span className="entry-health-pill" style={{ color: ch.color }}>
                        <Eye size={12} /> Camera: {ch.label}
                      </span>
                    )}
                    {device.diskUsagePercent != null && (
                      <span className="entry-health-pill" style={{
                        color: device.diskUsagePercent > 90 ? '#ef4444' : device.diskUsagePercent > 70 ? '#f59e0b' : '#22c55e'
                      }}>
                        <HardDrive size={12} /> Disk: {Math.round(device.diskUsagePercent)}%
                      </span>
                    )}
                    {device.networkLatencyMs != null && (
                      <span className="entry-health-pill" style={{
                        color: device.networkLatencyMs > 500 ? '#ef4444' : device.networkLatencyMs > 200 ? '#f59e0b' : '#22c55e'
                      }}>
                        <Wifi size={12} /> {device.networkLatencyMs}ms
                      </span>
                    )}
                  </div>
                )}

                <div className="entry-device-footer">
                  <span className="entry-device-lastseen">
                    <Clock size={12} /> {timeAgo(device.lastHeartbeat)}
                  </span>
                  <ChevronRight size={16} style={{ opacity: 0.3 }} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Enroll Modal */}
      {showEnrollModal && (
        <div className="platform-modal-overlay" onClick={() => setShowEnrollModal(false)}>
          <div className="platform-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '480px' }}>
            <div className="platform-modal-header">
              <h3><Plus size={18} /> Enroll New Tablet</h3>
              <button onClick={() => setShowEnrollModal(false)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
            </div>
            <div className="platform-modal-body">
              {/* Mode tabs */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                <button
                  className={'btn btn-sm ' + (enrollMode === 'qr' ? 'btn-primary' : 'btn-secondary')}
                  onClick={() => { setEnrollMode('qr'); setQrData(null); }}
                >📷 Scan QR Code</button>
                <button
                  className={'btn btn-sm ' + (enrollMode === 'token' ? 'btn-primary' : 'btn-secondary')}
                  onClick={() => { setEnrollMode('token'); setEnrollToken(null); }}
                >🔑 Use Token</button>
              </div>

              {enrollMode === 'qr' ? (
                <div style={{ textAlign: 'center' }}>
                  <p style={{ color: 'var(--text-secondary)', marginBottom: '16px', fontSize: '14px' }}>
                    Show this QR code to the tablet installer. They scan it with the {entryProductName} app to auto-enroll — no typing needed.
                  </p>
                  {qrPairing ? (
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ background: 'rgba(0,212,255,0.08)', border: '1px solid rgba(0,212,255,0.3)', borderRadius: 8, padding: '16px', marginBottom: 12 }}>
                        <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>TABLET SCANNED -- PENDING APPROVAL</div>
                        <div style={{ fontSize: 32, fontFamily: 'monospace', fontWeight: 700, color: '#00D4FF', letterSpacing: 4, marginBottom: 8 }}>{qrPairing.pairingCode}</div>
                        <div style={{ fontSize: 13, color: '#fff', marginBottom: 2 }}>{qrPairing.deviceInfo?.manufacturer} {qrPairing.deviceInfo?.model}</div>
                        <div style={{ fontSize: 12, color: '#888' }}>
                          {qrPairing.deviceInfo?.ipAddress && <span>IP: {qrPairing.deviceInfo.ipAddress} &nbsp;</span>}
                          <span>v{qrPairing.deviceInfo?.appVersion}</span>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button className="btn btn-success" style={{ flex: 1 }} disabled={acceptingQr}
                          onClick={async () => {
                            setAcceptingQr(true);
                            try {
                              const r = await fetchWithTenant(`/api/entry/pair/${qrPairing.id}/accept`, { method: 'POST', headers: {'Content-Type':'application/json'}, body: JSON.stringify({}) });
                              if (r.ok) { setShowEnrollModal(false); setQrData(null); setQrPairing(null); fetchDashboard(); fetchPendingPairings(); }
                              else { const e = await r.json(); alert(e.error || 'Failed'); }
                            } finally { setAcceptingQr(false); }
                          }}>
                          {acceptingQr ? 'Accepting...' : '✓ Accept'}
                        </button>
                        <button className="btn btn-danger" style={{ flex: 1 }}
                          onClick={async () => {
                            await fetchWithTenant(`/api/entry/pair/${qrPairing.id}`, { method: 'DELETE' });
                            setQrPairing(null); setQrData(null);
                          }}>
                          ✕ Reject
                        </button>
                      </div>
                    </div>
                  ) : !qrData ? (
                    <button className="btn btn-primary" onClick={handleFetchQr} disabled={loadingQr}>
                      {loadingQr ? 'Generating...' : 'Generate QR Code'}
                    </button>
                  ) : (
                    <>
                      <img src={qrData.dataUrl} alt="Pairing QR Code" style={{ width: 220, height: 220, borderRadius: 8, marginBottom: 12 }} />
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>
                        Expires: {new Date(qrData.expiresAt).toLocaleTimeString()}
                      </div>
                      <button className="btn btn-secondary btn-sm" onClick={handleFetchQr} disabled={loadingQr}>
                        Refresh QR
                      </button>
                    </>
                  )}
                </div>
              ) : !enrollToken ? (
                <>
                  <p style={{ color: 'var(--text-secondary)', marginBottom: '16px', fontSize: '14px' }}>
                    Generate a one-time enrollment token. Enter this on the tablet's admin panel to connect it to CCM.
                  </p>
                  <div className="form-group">
                    <label>Site Label (optional)</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="e.g., Main Lobby, Building A"
                      value={enrollForm.siteLabel}
                      onChange={e => setEnrollForm(prev => ({ ...prev, siteLabel: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label>Token Expires In</label>
                    <select
                      className="form-select"
                      value={enrollForm.ttlHours}
                      onChange={e => setEnrollForm(prev => ({ ...prev, ttlHours: parseInt(e.target.value) }))}
                    >
                      <option value={1}>1 hour</option>
                      <option value={4}>4 hours</option>
                      <option value={24}>24 hours</option>
                      <option value={72}>3 days</option>
                      <option value={168}>7 days</option>
                    </select>
                  </div>
                </>
              ) : (
                <div className="entry-token-display">
                  <p style={{ color: 'var(--text-secondary)', marginBottom: '12px', fontSize: '14px' }}>
                    Enter this token on the tablet's admin panel under "{entryProductName}" to enroll it.
                  </p>
                  <div className="entry-token-box">
                    <code>{enrollToken.token}</code>
                    <button className="btn btn-secondary btn-sm" onClick={copyToken}>Copy</button>
                  </div>
                  <div className="entry-token-meta">
                    {enrollToken.siteLabel && <span>Site: {enrollToken.siteLabel}</span>}
                    <span>Expires: {new Date(enrollToken.expiresAt).toLocaleString()}</span>
                  </div>
                </div>
              )}
            </div>
            <div className="platform-modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowEnrollModal(false)}>
                {enrollToken ? 'Done' : 'Cancel'}
              </button>
              {!enrollToken && (
                <button className="btn btn-primary" onClick={handleGenerateToken} disabled={generatingToken}>
                  {generatingToken ? 'Generating...' : 'Generate Token'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {showPrompt && (
        <TutorialPrompt
          module={entryFleetTutorial.module}
          moduleName={entryFleetTutorial.moduleName}
          steps={entryFleetTutorial.steps}
          onClose={dismissTutorial}
        />
      )}
    </div>
  );
};

export default EntryFleet;
